import React from 'react';
import { cx } from '../../shared/utils';

export type LoadingIndicatorSize = 'small' | 'medium' | 'large';
export type LoadingIndicatorColor = 'default' | 'inverse' | 'current';

export interface LoadingIndicatorProps {
  /**
   * Size of the loading indicator
   * - small: 12px (for tags)
   * - medium: 16px (for inputs)
   * - large: 20px (for buttons)
   */
  size?: LoadingIndicatorSize;
  
  /**
   * Color scheme for the spinner
   * - default: Uses --core-icon-secondary
   * - inverse: Uses --core-icon-inverse
   * - current: Uses currentColor (inherits from parent)
   */
  color?: LoadingIndicatorColor;
  
  /**
   * Additional CSS class names
   */
  className?: string;
  
  /**
   * Accessibility label for screen readers
   */
  ariaLabel?: string;
}

export const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
  size = 'medium',
  color = 'default',
  className,
  ariaLabel = 'Loading'
}) => {
  // Get the pixel size based on the size prop
  const pixelSize = size === 'small' ? 12 : size === 'medium' ? 16 : 20;
  
  const classes = cx(
    'core-loading-indicator',
    `core-loading-indicator--${size}`,
    `core-loading-indicator--${color}`,
    className
  );
  
  return (
    <div className={classes} aria-label={ariaLabel}>
      <svg
        className="core-loading-indicator__spinner"
        width={pixelSize}
        height={pixelSize}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background circle (lighter) */}
        <circle
          className="core-loading-indicator__track"
          cx="50"
          cy="50"
          r="44"
          fill="none"
          strokeWidth="8"
        />
        {/* Animated spinning circle */}
        <circle
          className="core-loading-indicator__path"
          cx="50"
          cy="50"
          r="44"
          fill="none"
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray="135 90"
        />
      </svg>
    </div>
  );
};

export default LoadingIndicator;
